import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { LoadingV2 } from "@ucl/library";
import InviteApiClient from "@/api/inviteApiClient";
import { InviteTokenResponse } from "@/api/inviteApiClient";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import "./ConsumerInvitePage.scss";

export const ConsumerInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const [error, setError] = useState<string | null>(null);

  const inviteApiClient = useAuthenticatedApiClient(InviteApiClient);

  useEffect(() => {
    const handleInviteRedirect = async () => {
      if (!token) {
        setError("Invalid invite link. Missing token.");
        return;
      }

      try {
        const response: InviteTokenResponse = await inviteApiClient.validateInviteToken(token);

        const { auth0OrganizationId, emailAddress } = response;

        // Pass token into both authorizationParams and appState
        await loginWithRedirect({
          authorizationParams: {
            login_hint: emailAddress,
            screen_hint: "signup",
            invite_token: token,
            organization: auth0OrganizationId,
          },
          appState: {
            returnTo: "/customer/portal",
          },
        });
      } catch (err) {
        console.error("Failed to validate invite token:", err);
        setError(
          "Invalid or expired invite link. Please contact the person who sent you this invitation."
        );
      }
    };

    handleInviteRedirect();
  }, [token, loginWithRedirect, inviteApiClient]);

  if (error) {
    return (
      <div className="consumer-invite-error">
        <div className="consumer-invite-error__content">
          <h2>Invalid Invitation</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/")} className="consumer-invite-error__button">
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consumer-invite">
      <div className="consumer-invite__content">
        <LoadingV2 />
        <h2>Processing Your Invitation</h2>
        <p>Please wait while we validate your invitation and redirect you to sign up...</p>
      </div>
    </div>
  );
};
